/* eslint-disable tailwindcss/no-custom-classname, tailwindcss/no-arbitrary-value, react/no-array-index-key */
import { MaterialIcon } from '../../components/MaterialIcon';
import type { TrustStripItem, TrustStripProps } from './types';

/**
 * M3a Trust Strip (CMS-1641) — a presentational row of 1–3 cross-product trust
 * factors (icon + short label) shown above the plan tier cards (M3b). No click,
 * hover, or selection behaviour.
 *
 * Trip-type set selection and the visibility / hide-when-empty rules are owned
 * by the fe-services wrapper; this component renders whatever resolved `items`
 * it is given and renders nothing for an empty list.
 *
 * Layout (Figma CMS-1641, node 13044:49849): a single edge-to-edge row
 * (`justify-between`) with a thin vertical divider between adjacent items. The
 * container gap is a symmetric floor so dividers never butt against an adjacent
 * icon when `justify-between` runs out of free space. Icons use the brand
 * primary colour at 18px, growing to 20px from `md` up; labels use the
 * breadcrumb typography token, which is itself responsive (12/16 → 14/18).
 * Horizontal padding comes from the page content container; the strip supplies
 * its own vertical padding (Figma `spacing/padding/y-sm`) so it reads as a block
 * rather than sitting flush against its neighbours.
 *
 * Items and dividers are emitted as flat, direct flex children (not nested) so
 * the container `gap` applies between every element. Keys are index-based: the
 * list is fixed and editor-ordered (≤3, no reorder/state), and icon/label can
 * repeat, so the index is the only stable unique key.
 */
const renderCell = (item: TrustStripItem, index: number): JSX.Element => (
  <div
    key={`item-${item.icon}-${index}`}
    className="flex items-center gap-spacing/gap/soft-6px"
  >
    {/* Decorative: no aria-label → MaterialIcon emits aria-hidden. The adjacent
        label carries the meaning. `shrink-0` is required: MaterialIcon's span
        has `overflow:hidden`, which gives a flex item `min-width:0`, so on a
        tight row the glyph would otherwise be squeezed and clipped. */}
    <MaterialIcon
      icon={item.icon}
      className="!text-[18px] shrink-0 text-buttons/primary md:!text-[20px]"
    />
    <span className="font-font-weight/breadcrumb text-font-size/breadcrumb leading-line-height/breadcrumb text-[#4a4a4a]">
      {item.label}
    </span>
  </div>
);

export const TrustStrip = ({ items }: TrustStripProps): JSX.Element | null => {
  if (items.length === 0) {
    return null;
  }

  return (
    <div
      data-component="trust-strip"
      className="flex items-center justify-between gap-spacing/gap/x-sm py-spacing/padding/y-sm"
    >
      {items.flatMap((item, index) =>
        index === 0
          ? [renderCell(item, index)]
          : [
              <div
                key={`divider-${index}`}
                data-component="trust-strip-divider"
                aria-hidden="true"
                className="h-[18px] w-px shrink-0 bg-[#e5e7eb]"
              />,
              renderCell(item, index),
            ],
      )}
    </div>
  );
};
